/**
 * Trusted Circle (§22.5) management page.
 *
 * Owner-only: invite grandparents, babysitters, school drivers, etc., with
 * scoped permissions. Other members in the circle see a read-only list.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Copy,
  Link as LinkIcon,
  Mail,
  Pencil,
  Trash2,
  UserPlus2,
  HeartHandshake,
  Clock,
  AlertTriangle,
  History,
  Loader2,
  Siren,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';
import { cn } from '@/lib/utils';
import {
  ALL_PERMISSIONS,
  PERMISSION_COPY,
  ROLE_COPY,
  ROLE_DEFAULTS,
  circleApi,
  type CircleInvitation,
  type CircleMember,
  type CirclePermission,
  type CircleRole,
  type CircleSchedule,
} from '@/api/circle';

const ROLE_OPTIONS: CircleRole[] = [
  'parent',
  'grandparent',
  'babysitter',
  'school_driver',
  'aunt_uncle',
  'custom',
];

/** Color per role — keeps members visually distinct at a glance. Owners
 *  get the brand color; everyone else gets a hue that hints at their role
 *  (warm for family, cool for hired help, etc). */
const ROLE_COLOR: Record<CircleRole, string> = {
  owner: '#4f6bff',         // brand indigo
  parent: '#0ea5e9',        // sky
  grandparent: '#a855f7',   // violet
  babysitter: '#14b8a6',    // teal
  school_driver: '#f59e0b', // amber
  aunt_uncle: '#ec4899',    // pink
  custom: '#64748b',        // slate
};

function inviteLinkFor(token: string): string {
  return `${window.location.origin}/invite/${token}`;
}

function PermissionList({ perms }: { perms: CirclePermission[] }) {
  if (perms.length === 0) {
    return <span className="text-xs italic text-[color:var(--kz-color-ink-muted)]">no permissions</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {perms.map((p) => (
        <span
          key={p}
          className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--kz-color-ink-secondary)]"
        >
          {PERMISSION_COPY[p].label}
        </span>
      ))}
    </div>
  );
}

function ScheduleSummary({ schedule }: { schedule: CircleSchedule | null }) {
  if (!schedule) return null;
  const days = formatDayMask(schedule.days);
  const from = formatMinute(schedule.from_min);
  const to = formatMinute(schedule.to_min);
  return (
    <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
      Active {days} · {from}–{to} UTC
    </p>
  );
}

function formatMinute(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function formatDayMask(mask: number): string {
  if ((mask & 0x7F) === 0x7F) return 'every day';
  if ((mask & 0x7F) === 0x1F) return 'Mon–Fri';
  if ((mask & 0x7F) === 0x60) return 'weekends';
  return DAYS.filter((_, i) => mask & (1 << i)).join(', ');
}

// ── Invite / edit dialog state ───────────────────────────────────────────────

interface InviteDraft {
  email: string;
  display_name: string;
  role: CircleRole;
  permissions: Set<CirclePermission>;
  schedule_enabled: boolean;
  days: number;
  from_min: number;
  to_min: number;
}

function defaultDraft(): InviteDraft {
  return {
    email: '',
    display_name: '',
    role: 'grandparent',
    permissions: new Set(ROLE_DEFAULTS.grandparent),
    schedule_enabled: false,
    days: 0x1F,
    from_min: 16 * 60,
    to_min: 22 * 60,
  };
}

function fromMember(m: CircleMember): InviteDraft {
  return {
    email: '',
    display_name: m.display_name ?? '',
    role: m.role,
    permissions: new Set(m.permissions),
    schedule_enabled: m.schedule !== null,
    days: m.schedule?.days ?? 0x1F,
    from_min: m.schedule?.from_min ?? 16 * 60,
    to_min: m.schedule?.to_min ?? 22 * 60,
  };
}

interface DraftDialogProps {
  open: boolean;
  mode: 'invite' | 'edit';
  initial: InviteDraft;
  editingMember?: CircleMember;
  onClose: () => void;
  onSubmit: (draft: InviteDraft) => void;
  submitting: boolean;
}

function DraftDialog({ open, mode, initial, editingMember, onClose, onSubmit, submitting }: DraftDialogProps) {
  const [draft, setDraft] = useState<InviteDraft>(initial);
  // Permissions list is collapsed by default — most parents pick a role
  // and accept the role's preset, no need to scan 9 switches.
  const [showAllPerms, setShowAllPerms] = useState(false);

  // Reset draft when the dialog reopens with a different `initial`.
  // We key off `open` flipping true.
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setDraft(initial);
      // For edit mode, open the perms section so the parent sees current state;
      // for fresh invites, keep it collapsed.
      setShowAllPerms(mode === 'edit');
    }
  }

  const togglePerm = (p: CirclePermission) => {
    setDraft((d) => {
      const next = new Set(d.permissions);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return { ...d, permissions: next };
    });
  };

  const onRoleChange = (role: CircleRole) => {
    setDraft((d) => ({ ...d, role, permissions: new Set(ROLE_DEFAULTS[role]) }));
  };

  const toggleDay = (i: number) => {
    setDraft((d) => ({ ...d, days: d.days ^ (1 << i) }));
  };

  const selectedPerms = Array.from(draft.permissions);
  const usingRoleDefault =
    selectedPerms.length === ROLE_DEFAULTS[draft.role].length &&
    selectedPerms.every((p) => ROLE_DEFAULTS[draft.role].includes(p));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Cap at 90vh + flex column so header/footer pin and only the body
          scrolls when "Customize" is open or the user is on a short window. */}
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-lg flex-col gap-0 p-0 sm:w-auto">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>
            {mode === 'invite' ? 'Invite a trusted contact' : `Edit ${editingMember?.display_name ?? 'member'}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'invite'
              ? 'Send a one-time link. They keep their own login; you pick what they can see.'
              : 'Adjust this member’s role and permissions. Changes are immediate.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 overflow-y-auto px-6 py-4">
          {/* Email + name on one row on wider screens. */}
          <div className={cn('grid gap-3', mode === 'invite' && 'sm:grid-cols-2')}>
            {mode === 'invite' && (
              <div className="grid gap-1.5">
                <Label htmlFor="inv-email">Email</Label>
                <Input
                  id="inv-email"
                  type="email"
                  placeholder="grandma@example.com"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
              </div>
            )}
            <div className="grid gap-1.5">
              <Label htmlFor="inv-name">
                Display name <span className="text-[color:var(--kz-color-ink-muted)]">(optional)</span>
              </Label>
              <Input
                id="inv-name"
                placeholder="Grandma Helen"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              />
            </div>
          </div>

          {/* Role as a single horizontal pill row — hint text moves below. */}
          <div className="grid gap-2">
            <Label>Role</Label>
            <div className="flex flex-wrap gap-1.5">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => onRoleChange(r)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    draft.role === r
                      ? 'border-[color:var(--kz-color-brand-primary)] bg-[color:var(--kz-color-brand-primary)]/10 text-[color:var(--kz-color-brand-primary)]'
                      : 'border-[color:var(--kz-color-border-subtle)] text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
                  )}
                >
                  {ROLE_COPY[r].label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              {ROLE_COPY[draft.role].hint}
            </p>
          </div>

          {/* What this person will see — summary chips. Collapsible "Customize"
              opens the full per-permission switch list. */}
          <div className="rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)]/40 p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">They&rsquo;ll be able to</p>
              <button
                type="button"
                onClick={() => setShowAllPerms((v) => !v)}
                className="text-xs font-medium text-[color:var(--kz-color-brand-primary)] hover:underline"
              >
                {showAllPerms ? 'Hide details' : 'Customize'}
              </button>
            </div>
            {selectedPerms.length === 0 ? (
              <p className="mt-1.5 text-xs italic text-[color:var(--kz-color-ink-muted)]">
                Nothing. They&rsquo;ll see the family name only.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedPerms.map((p) => (
                  <span
                    key={p}
                    className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-[color:var(--kz-color-ink-secondary)] shadow-sm ring-1 ring-[color:var(--kz-color-border-subtle)]/60"
                  >
                    {PERMISSION_COPY[p].label}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1.5 text-[11px] text-[color:var(--kz-color-ink-muted)]">
              {usingRoleDefault
                ? `Defaults for ${ROLE_COPY[draft.role].label}.`
                : 'Custom set — not the role default.'}
            </p>
          </div>

          {showAllPerms && (
            <div className="grid gap-2">
              <Separator />
              <p className="text-xs font-medium uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                All permissions
              </p>
              <div className="grid gap-1.5">
                {ALL_PERMISSIONS.map((p) => (
                  <label
                    key={p}
                    className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-[color:var(--kz-color-surface-sunken)]"
                  >
                    <Switch checked={draft.permissions.has(p)} onCheckedChange={() => togglePerm(p)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{PERMISSION_COPY[p].label}</p>
                      <p className="text-[11px] leading-snug text-[color:var(--kz-color-ink-muted)]">
                        {PERMISSION_COPY[p].hint}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {draft.permissions.has('view_location_scheduled') && (
            <>
              <Separator />
              <div className="grid gap-3 rounded-md bg-[color:var(--kz-color-surface-sunken)] p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Time window</p>
                    <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                      Required for “See location during schedule”.
                    </p>
                  </div>
                  <Switch
                    checked={draft.schedule_enabled}
                    onCheckedChange={(v) => setDraft({ ...draft, schedule_enabled: v })}
                  />
                </div>
                {draft.schedule_enabled && (
                  <div className="grid gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {DAYS.map((d, i) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDay(i)}
                          className={cn(
                            'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                            draft.days & (1 << i)
                              ? 'bg-[color:var(--kz-color-brand-primary)] text-white'
                              : 'bg-white text-[color:var(--kz-color-ink-secondary)] border border-[color:var(--kz-color-border-subtle)]',
                          )}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <Label className="text-xs">From (UTC)</Label>
                        <Input
                          type="time"
                          value={formatMinute(draft.from_min)}
                          onChange={(e) => {
                            const parts = e.target.value.split(':');
                            const h = parseInt(parts[0] ?? '', 10);
                            const m = parseInt(parts[1] ?? '', 10);
                            if (!Number.isNaN(h) && !Number.isNaN(m)) {
                              setDraft({ ...draft, from_min: h * 60 + m });
                            }
                          }}
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">To (UTC)</Label>
                        <Input
                          type="time"
                          value={formatMinute(draft.to_min)}
                          onChange={(e) => {
                            const parts = e.target.value.split(':');
                            const h = parseInt(parts[0] ?? '', 10);
                            const m = parseInt(parts[1] ?? '', 10);
                            if (!Number.isNaN(h) && !Number.isNaN(m)) {
                              setDraft({ ...draft, to_min: h * 60 + m });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-[color:var(--kz-color-border-subtle)] px-6 py-4">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(draft)} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'invite' ? 'Send invite' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CirclePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<CircleMember | null>(null);
  // Member queued for removal — drives the destructive confirm dialog that
  // replaces the rough-edged native `confirm()` we used before.
  const [removing, setRemoving] = useState<CircleMember | null>(null);

  const members = useQuery({ queryKey: ['circle', 'members'], queryFn: () => circleApi.listMembers(), retry: false });
  // Owner identity comes from the backend `is_caller` flag — the server is
  // the only place that can map JWT sub → membership row. We then only query
  // invitations when we know the caller is the owner, since the endpoint
  // 403s non-owners and we want to avoid spurious red toasts.
  const me = members.data?.find((m) => m.is_caller) ?? null;
  const isOwner = me?.role === 'owner';

  const invitations = useQuery({
    queryKey: ['circle', 'invitations'],
    queryFn: () => circleApi.listInvitations(),
    retry: false,
    enabled: isOwner,
  });

  const createInvite = useMutation({
    mutationFn: (draft: InviteDraft) =>
      circleApi.createInvitation({
        email: draft.email.trim().toLowerCase(),
        display_name: draft.display_name || undefined,
        role: draft.role,
        permissions: Array.from(draft.permissions),
        schedule:
          draft.permissions.has('view_location_scheduled') && draft.schedule_enabled
            ? { days: draft.days & 0x7F, from_min: draft.from_min, to_min: draft.to_min }
            : undefined,
      }),
    onSuccess: (created) => {
      toast.success(`Invite sent. Share the link with ${created.email}.`);
      const link = inviteLinkFor(created.token);
      void navigator.clipboard?.writeText(link).catch(() => undefined);
      setInviteOpen(false);
      void qc.invalidateQueries({ queryKey: ['circle', 'invitations'] });
    },
    onError: (e: Error) => toast.error(e.message.includes('already_invited') ? 'That email already has a pending invite.' : 'Could not send invite.'),
  });

  const updateMember = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: InviteDraft }) =>
      circleApi.updateMember(id, {
        role: draft.role,
        display_name: draft.display_name || null,
        permissions: Array.from(draft.permissions),
        schedule:
          draft.permissions.has('view_location_scheduled') && draft.schedule_enabled
            ? { days: draft.days & 0x7F, from_min: draft.from_min, to_min: draft.to_min }
            : null,
      }),
    onSuccess: () => {
      toast.success('Member updated.');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['circle', 'members'] });
    },
    onError: () => toast.error('Could not update member.'),
  });

  const removeMember = useMutation({
    mutationFn: (id: string) => circleApi.removeMember(id),
    onSuccess: () => {
      toast.success('Member removed.');
      void qc.invalidateQueries({ queryKey: ['circle', 'members'] });
    },
    onError: () => toast.error('Could not remove member.'),
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) => circleApi.revokeInvitation(id),
    onSuccess: () => {
      toast.success('Invitation revoked.');
      void qc.invalidateQueries({ queryKey: ['circle', 'invitations'] });
    },
    onError: () => toast.error('Could not revoke invitation.'),
  });

  // Same DELETE endpoint as revoke, but used on history rows where the
  // server hard-deletes (status is no longer "pending"). UI says "Delete"
  // because that's what's actually happening to the row.
  const deleteInvite = useMutation({
    mutationFn: (id: string) => circleApi.revokeInvitation(id),
    onSuccess: () => {
      toast.success('Invitation deleted.');
      void qc.invalidateQueries({ queryKey: ['circle', 'invitations'] });
    },
    onError: () => toast.error('Could not delete invitation.'),
  });

  const copyLink = (inv: CircleInvitation) => {
    const link = inviteLinkFor(inv.token);
    void navigator.clipboard?.writeText(link).then(
      () => toast.success('Invite link copied.'),
      () => toast.error('Clipboard unavailable.'),
    );
  };

  const pendingInvites = (invitations.data ?? []).filter((i) => i.status === 'pending');
  // Anything not pending — accepted / declined / revoked / expired — that
  // the owner can scroll back through.
  const historyInvites = (invitations.data ?? []).filter((i) => i.status !== 'pending');
  // Only the parent (owner) is in the circle at the start. When that's the
  // only member AND no invites are out yet, this is the first-time state
  // where we want the explainer card up top and a CTA-driven empty list.
  const nonOwnerMembers = (members.data ?? []).filter((m) => m.role !== 'owner');
  const isFirstTime =
    !members.isPending && nonOwnerMembers.length === 0 && pendingInvites.length === 0;

  const INVITE_EXPIRY_SOON_MS = 24 * 60 * 60 * 1000; // 1 day
  const isExpiringSoon = (iso: string) =>
    new Date(iso).getTime() - Date.now() < INVITE_EXPIRY_SOON_MS;

  const formatInviteStatus = (s: CircleInvitation['status']) =>
    s === 'accepted'
      ? 'Accepted'
      : s === 'declined'
        ? 'Declined'
        : s === 'revoked'
          ? 'Revoked'
          : s === 'expired'
            ? 'Expired'
            : s;
  const STATUS_CHIP: Record<string, string> = {
    accepted: 'bg-emerald-100 text-emerald-800',
    declined: 'bg-slate-200 text-slate-700',
    revoked: 'bg-slate-200 text-slate-700',
    expired: 'bg-amber-100 text-amber-800',
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.circle.title')}
        icon={HeartHandshake}
        tint="coral"
        description={t('pages.circle.description')}
        actions={
          isOwner && (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus2 className="size-4" />
              Invite
            </Button>
          )
        }
      />

      {/* First-time hero: when nothing exists yet, lead with the explainer +
          a big call-to-action so the page never feels empty. */}
      {isFirstTime && isOwner && (
        <Card className="mt-8 border-[#ff7a59]/20 bg-[#ff7a59]/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <HeartHandshake className="size-5 text-[#ff7a59]" />
              Build your child&rsquo;s safety net
            </CardTitle>
            <CardDescription>
              Invite grandparents, babysitters, drivers, or godparents. Each gets a
              one-time link, picks their own login, and only sees what you allow.
              Pick from preset roles or compose custom permissions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus2 className="size-4" />
              Invite your first contact
            </Button>
          </CardContent>
        </Card>
      )}

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
          Members
        </h2>
        <div className="mt-3 grid gap-3">
          {members.isPending && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full rounded-md" />
              <Skeleton className="h-12 w-full rounded-md" />
            </div>
          )}
          {!members.isPending && nonOwnerMembers.length === 0 && (
            <Card>
              <EmptyState
                icon={UserPlus2}
                tint="coral"
                title="No one else in the circle yet"
                description={
                  isOwner
                    ? 'Invite a grandparent or trusted contact — they get scoped, time-limited access.'
                    : 'When the family owner adds people, they’ll appear here.'
                }
                action={
                  isOwner ? (
                    <Button onClick={() => setInviteOpen(true)}>
                      <UserPlus2 className="size-4" />
                      Invite someone
                    </Button>
                  ) : undefined
                }
              />
            </Card>
          )}
          {members.data?.map((m) => {
            // Member is a SOS-alert recipient → render a prominent rose
            // badge separate from the regular permission chip row. This is
            // the most safety-critical permission so it deserves its own
            // visual treatment.
            const receivesSos = m.permissions.includes('receive_sos');
            const initials = (m.display_name ?? ROLE_COPY[m.role].label).slice(0, 2).toUpperCase();
            return (
            <Card key={m.id}>
              <CardContent className="flex flex-wrap items-start gap-4 p-4">
                <span
                  className="grid size-10 shrink-0 place-items-center rounded-full text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: ROLE_COLOR[m.role] }}
                >
                  {initials}
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{m.display_name ?? ROLE_COPY[m.role].label}</p>
                    <span className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[color:var(--kz-color-ink-secondary)]">
                      {ROLE_COPY[m.role].label}
                    </span>
                    {m.is_caller && (
                      <span className="rounded-full bg-[color:var(--kz-color-brand-primary)]/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[color:var(--kz-color-brand-primary)]">
                        You
                      </span>
                    )}
                    {receivesSos && m.role !== 'owner' && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rose-700"
                        title="Receives SOS alerts when a child triggers panic"
                      >
                        <Siren className="size-3" />
                        Receives SOS
                      </span>
                    )}
                  </div>
                  {m.role !== 'owner' ? (
                    <>
                      <PermissionList perms={m.permissions} />
                      <ScheduleSummary schedule={m.schedule} />
                    </>
                  ) : (
                    <p className="text-xs text-[color:var(--kz-color-ink-muted)]">Full access · cannot be removed.</p>
                  )}
                </div>
                {isOwner && m.role !== 'owner' && (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => setEditing(m)}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Remove"
                      onClick={() => setRemoving(m)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </section>

      {isOwner && !isFirstTime && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
            Pending invitations
          </h2>
          {pendingInvites.length === 0 ? (
            <Card className="mt-3">
              <EmptyState
                icon={Mail}
                tint="slate"
                title="No pending invitations"
                description="When you invite someone, their link appears here until they accept or it expires."
              />
            </Card>
          ) : (
            <div className="mt-3 grid gap-3">
              {pendingInvites.map((inv) => {
                const soon = isExpiringSoon(inv.expires_at);
                return (
                  <Card key={inv.id}>
                    <CardContent className="flex flex-wrap items-start gap-4 p-4">
                      <Mail className="mt-1 size-5 text-[color:var(--kz-color-ink-muted)]" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{inv.email}</p>
                          {soon && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                              <AlertTriangle className="size-3" />
                              Expires soon
                            </span>
                          )}
                        </div>
                        <p className="flex items-center gap-1 text-xs text-[color:var(--kz-color-ink-muted)]">
                          <Clock className="size-3" />
                          {ROLE_COPY[inv.role].label} · expires {new Date(inv.expires_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <PermissionList perms={inv.permissions} />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => copyLink(inv)}>
                          <Copy className="size-3.5" /> Copy link
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeInvite.mutate(inv.id)}
                          disabled={revokeInvite.isPending}
                        >
                          Revoke
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      )}

      {isOwner && historyInvites.length > 0 && (
        <section className="mt-10">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
            <History className="size-3.5" />
            Invitation history
          </h2>
          <div className="mt-3 grid gap-2">
            {historyInvites.map((inv) => (
              <div
                key={inv.id}
                className="flex flex-wrap items-center gap-3 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] px-4 py-2.5"
              >
                <Mail className="size-4 text-[color:var(--kz-color-ink-muted)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{inv.email}</p>
                  <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                    {ROLE_COPY[inv.role].label} · {new Date(inv.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    STATUS_CHIP[inv.status] ?? 'bg-slate-200 text-slate-700',
                  )}
                >
                  {formatInviteStatus(inv.status)}
                </span>
                {/* Accepted invitations are the audit record for how a
                    member joined — backend rejects DELETE on them, so we
                    don't render the trash icon at all. */}
                {inv.status !== 'accepted' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete invitation"
                    title="Delete from history"
                    disabled={deleteInvite.isPending}
                    onClick={() => deleteInvite.mutate(inv.id)}
                    className="text-[color:var(--kz-color-ink-muted)] hover:bg-rose-50 hover:text-rose-700"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {!isFirstTime && (
        <Card className="mt-10">
          <CardHeader>
            <CardTitle className="text-base">How invites work</CardTitle>
            <CardDescription>
              <span className="inline-flex items-center gap-1.5"><LinkIcon className="size-3.5" /> One-time link, valid for 7 days.</span>
              <span className="ml-3">Recipient signs in (or signs up) before accepting.</span>
              <span className="ml-3">Permissions take effect immediately on acceptance.</span>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <DraftDialog
        open={inviteOpen}
        mode="invite"
        initial={defaultDraft()}
        onClose={() => setInviteOpen(false)}
        onSubmit={(d) => createInvite.mutate(d)}
        submitting={createInvite.isPending}
      />
      <DraftDialog
        open={editing !== null}
        mode="edit"
        initial={editing ? fromMember(editing) : defaultDraft()}
        editingMember={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSubmit={(d) => editing && updateMember.mutate({ id: editing.id, draft: d })}
        submitting={updateMember.isPending}
      />

      {/* Destructive Remove confirm — replaces the native browser confirm()
          so the visual stays consistent with the rest of the app. */}
      <Dialog
        open={removing !== null}
        onOpenChange={(o) => {
          if (!o && !removeMember.isPending) setRemoving(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="size-4" />
              Remove {removing?.display_name ?? ROLE_COPY[removing?.role ?? 'custom'].label}?
            </DialogTitle>
            <DialogDescription>
              They&rsquo;ll lose access to your family&rsquo;s circle immediately.
              SOS alerts, location windows, and chat permissions are revoked.
              You can re-invite them later if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRemoving(null)}
              disabled={removeMember.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (removing) {
                  removeMember.mutate(removing.id, {
                    onSuccess: () => setRemoving(null),
                  });
                }
              }}
              disabled={removeMember.isPending}
            >
              {removeMember.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Removing
                </>
              ) : (
                <>
                  <Trash2 className="size-4" />
                  Remove from circle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
